import React, { useState, useEffect } from 'react';
import {
    Button,
    DatePicker,
    Form,
    Input,
    Col,
    Row,
    Card,
} from 'antd';
import dayjs from 'dayjs';
import { Select } from 'antd';
import { useSelector } from 'react-redux';
import * as adminactions from "store/actions/AuthActions";
import { useNavigate, useParams } from 'react-router-dom';
import TextArea from 'antd/lib/input/TextArea';

// Dates travel as 'YYYY-MM-DD' strings but the DatePicker needs dayjs values
const DATE_FIELDS = ['birth_date', 'marriage_anniversary_date'];
const API_DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE_FORMAT = 'DD-MM-YYYY';


const ContactForm = () => {

    const { id } = useParams();
    const navigate = useNavigate();
    const [dataSource, setDataSource] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [vendors, setVendors] = useState([]);

    const country_options = useSelector(store => store.master?.countries);


    const onFinish = (values) => {
        if (values) {
            const payload = { ...values };
            DATE_FIELDS.forEach((field) => {
                payload[field] = payload[field] ? dayjs(payload[field]).format(API_DATE_FORMAT) : null;
            });
            onSaveHandler(payload)
        }
    };

    const fetchAccounts = async () => {
        const response = await adminactions.loadAccounts(1, undefined, 1000)
        console.log(response)
        if (response && response.status === 200) {
            setAccounts(response.data);
        }
    }

    const fetchVendors = async () => {
        const response = await adminactions.loadVendors(1, undefined, 1000);
        console.log(response)
        if (response) {
            setVendors(response);
        }
    }


    const retrieveContact = async () => {
        if (id) {
            const response = await adminactions.retrieveContact(id);
            console.log(response)
            if (response) {
                console.log("Changing state now...")
                const instance = { ...response };
                DATE_FIELDS.forEach((field) => {
                    instance[field] = instance[field] ? dayjs(instance[field]) : null;
                });
                setDataSource(instance);
            }
        }
        setLoading(false);
    }

    useEffect(() => {
        retrieveContact();
        fetchAccounts();
        fetchVendors();
    }, [])

    // A Select renders its raw value when no option matches it, which is why
    // editing a contact briefly (or permanently, if the record sits outside the
    // fetched page) showed a UUID. Seed the current value from the labels the
    // contact API returns so there is always something readable to show.
    const accountOptions = (() => {
        const options = (accounts?.results || []).map((account) => ({
            value: account.id,
            label: [account.account_number, account.name].filter(Boolean).join(' - '),
        }));
        const current = dataSource?.account;
        if (current && !options.some((option) => option.value === current)) {
            options.unshift({
                value: current,
                label: dataSource?.account_label || dataSource?.account_name || current,
            });
        }
        return options;
    })();

    const vendorOptions = (() => {
        const options = (vendors?.results || []).map((vendor) => ({
            value: vendor.id,
            label: vendor.name,
        }));
        const current = dataSource?.vendor;
        if (current && !options.some((option) => option.value === current)) {
            options.unshift({ value: current, label: dataSource?.vendor_name || current });
        }
        return options;
    })();

    const onSaveHandler = async (values) => {
        if (id) {
            const response = await adminactions.updateContacts(
                values, id
            );
            console.log(response);
            if (response) {
                navigate("/contacts")
            }
        } else {
            const response = await adminactions.addContacts(
                values
            );
            console.log(response);
            if (response) {
                navigate("/contacts")
            }
        }
    }


    return (
        <div style={{
            height: 'calc(100vh - 120px)',
            overflow: 'auto',
            padding: '24px',
            background: '#f0f2f5'
        }}>
            {!loading && (
                <Card
                    title={id ? "Edit Contact" : "Add Contact"}
                    bordered={false}
                    style={{ maxWidth: 1200, margin: '0 auto' }}
                >
                    <Form
                        layout="vertical"
                        onFinish={onFinish}
                        initialValues={{
                            ...dataSource
                        }}
                    >
                        <Row gutter={[24, 0]}>
                            {/* Personal Information */}
                            <Col span={24}>
                                <h3 style={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    marginBottom: '16px',
                                    borderBottom: '2px solid #1890ff',
                                    paddingBottom: '8px'
                                }}>
                                    Personal Information
                                </h3>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="First Name" name='first_name'>
                                    <Input placeholder="Enter first name" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="Middle Name" name='middle_name'>
                                    <Input placeholder="Enter middle name" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="Last Name" name='last_name'>
                                    <Input placeholder="Enter last name" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="Company" name='company'>
                                    <Input placeholder="Enter company name" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="Job Title" name='job_title'>
                                    <Input placeholder="Enter job title" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="Birth Date" name='birth_date'>
                                    <DatePicker format={DISPLAY_DATE_FORMAT} style={{ width: '100%' }} placeholder="Select birth date" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="Marriage Anniversary" name='marriage_anniversary_date'>
                                    <DatePicker format={DISPLAY_DATE_FORMAT} style={{ width: '100%' }} placeholder="Select anniversary date" />
                                </Form.Item>
                            </Col>

                            {/* Contact Information */}
                            <Col span={24} style={{ marginTop: '24px' }}>
                                <h3 style={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    marginBottom: '16px',
                                    borderBottom: '2px solid #1890ff',
                                    paddingBottom: '8px'
                                }}>
                                    Contact Information
                                </h3>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="Email" name='email'>
                                    <Input type="email" placeholder="Enter email address" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="Alternate Email" name='alt_email'>
                                    <Input type="email" placeholder="Enter alternate email" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="Phone Number" name='phone_number'>
                                    <Input placeholder="Enter phone number" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="Alternate Phone" name='alt_phone_number'>
                                    <Input placeholder="Enter alternate phone" />
                                </Form.Item>
                            </Col>

                            {/* Relationships */}
                            <Col span={24} style={{ marginTop: '24px' }}>
                                <h3 style={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    marginBottom: '16px',
                                    borderBottom: '2px solid #1890ff',
                                    paddingBottom: '8px'
                                }}>
                                    Related Accounts & Vendors
                                </h3>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item label="Account" name='account'>
                                    <Select
                                        showSearch
                                        placeholder="Select an account"
                                        allowClear
                                        // A missing name would make this throw, and then the
                                        // whole dropdown stops filtering.
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={accountOptions}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item label="Vendor" name='vendor'>
                                    <Select
                                        showSearch
                                        placeholder="Select a vendor"
                                        allowClear
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={vendorOptions}
                                    />
                                </Form.Item>
                            </Col>

                            {/* Address Information */}
                            <Col span={24} style={{ marginTop: '24px' }}>
                                <h3 style={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    marginBottom: '16px',
                                    borderBottom: '2px solid #1890ff',
                                    paddingBottom: '8px'
                                }}>
                                    Address Information
                                </h3>
                            </Col>

                            <Col span={24}>
                                <Form.Item label="Address" name="address">
                                    <TextArea rows={3} placeholder="Enter complete address" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="City" name="city">
                                    <Input placeholder="Enter city" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="State" name="state">
                                    <Input placeholder="Enter state" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="Pincode" name="pincode">
                                    <Input placeholder="Enter pincode" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item label="Country" name="country">
                                    <Select
                                        showSearch
                                        placeholder="Search to Select"
                                        optionFilterProp="children"
                                        filterOption={(input, option) => (option?.label?.toLowerCase?.() ?? '').includes(input?.toLowerCase?.())}
                                        filterSort={(optionA, optionB) =>
                                            (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
                                        }
                                        loading={Array.isArray(country_options) && country_options?.length > 0 ? false : true}
                                        options={country_options?.map?.((opt, ind) => {
                                            return {
                                                label: `${opt.name}`,
                                                value: opt.id
                                            }
                                        }) || []}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item label="Website" name="website">
                                    <Input placeholder="Enter website URL" />
                                </Form.Item>
                            </Col>

                            {/* Additional Information */}
                            <Col span={24} style={{ marginTop: '24px' }}>
                                <h3 style={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    marginBottom: '16px',
                                    borderBottom: '2px solid #1890ff',
                                    paddingBottom: '8px'
                                }}>
                                    Additional Notes
                                </h3>
                            </Col>

                            <Col span={24}>
                                <Form.Item label="Notes" name="notes">
                                    <TextArea rows={4} placeholder="Enter any additional notes or comments" />
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* Submit Button - Fixed at bottom */}
                        <div style={{
                            marginTop: '32px',
                            paddingTop: '24px',
                            borderTop: '1px solid #f0f0f0',
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end'
                        }}>
                            <Button onClick={() => navigate('/contacts')}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" size="large">
                                {id ? 'Update Contact' : 'Create Contact'}
                            </Button>
                        </div>
                    </Form>
                </Card>
            )}
        </div>
    );
};
export default ContactForm;